import {
  ApiException,
  ApproverEmailsInputModel,
  ApproverValidationInputModel,
  ApproverValidationViewModel,
  CurrentUserInputModel,
  CustomerIndexViewModel,
  CustomersApi,
  CustomerViewModel,
  ManagerModel,
  PersonModel,
  PersonRole,
  TenantInputModel,
  TenantStatisticsViewModel,
  TenantViewModel,
  UnitAccountIndexViewModel,
  UnitAccountViewModel,
  UserInputModel,
  UserSettingsInputModel,
  UserSettingsViewModel,
  UserViewModel,
} from '@ail/customers-api'
import { createApi } from '@reduxjs/toolkit/query/react'
import { USER_MANAGER } from '../../auth/userManager'
import { Constants } from '../../helpers/Constants'
import { debounce } from '../../helpers/Utils'
import { getBaseQuery } from './apiQuery'

const client = new CustomersApi({
  baseUrl: Constants.customerEndpoint,
  getToken: async () => {
    const user = await USER_MANAGER.getUser()
    return user?.access_token ?? ''
  },
})

const putUserSettingsDebounced = debounce((model: UserSettingsInputModel) => {
  client.putUserSettings(model)
}, 500)

// prettier-ignore
export const customersApi = createApi({
  reducerPath: 'customersApi',
  baseQuery: getBaseQuery<ApiException>(),
  endpoints: (builder) => ({
    getAllCustomers: builder.query<CustomerIndexViewModel[], void>({
      query: () => client.allCustomers(),
    }),
    getUnassignedCustomers: builder.query<CustomerViewModel[], void>({
      query: () => client.getUnassignedCustomers(),
      providesTags: ['customers']
    }),
    getTenantUsers: builder.query<UserViewModel[], string>({
      query: (tenantId) => client.getUsers(tenantId),
      providesTags: ['users'],
    }),
    getCustomers: builder.query<CustomerViewModel[], string>({
      query: (tenantId) => client.getCustomers(tenantId),
      providesTags: ['customers'],
    }),
    getUser: builder.query<UserViewModel, { tenantId: string; userId: string }>({
      query: ({ tenantId, userId }) => client.getUser(tenantId, userId),
      providesTags: ['users'],
    }),
    getCurrentUser: builder.query<UserViewModel, void>({
      query: () => client.getCurrentUser(),
      providesTags: ['currentUser'],
    }),
    getUserTags: builder.query<string[], string>({
      query: (tenantId) => client.getUserTags(tenantId),
      providesTags: ['userTags'],
    }),
    postUser: builder.mutation<UserViewModel, { tenantId: string; model: UserInputModel }>({
      query: ({ tenantId, model }) => client.postUser(tenantId, model),
      invalidatesTags: ['users'],
    }),
    putUser: builder.mutation<UserViewModel, { tenantId: string; userId: string; model: UserInputModel }>({
      query: ({ tenantId, userId, model }) => client.putUser(tenantId, userId, model),
      invalidatesTags: ['users'],
    }),
    patchUserEnabled: builder.mutation<UserViewModel, { tenantId: string; userId: string; enabled: boolean }>({
      query: ({ tenantId, userId, enabled }) => client.patchUserEnabled(tenantId, userId, enabled),
      invalidatesTags: ['users'],
    }),
    deleteUser: builder.mutation<void, { tenantId: string; userId: string }>({
      query: ({ tenantId, userId }) => client.deleteUser(tenantId, userId),
      invalidatesTags: ['users'],
    }),
    patchCurrentUser: builder.mutation<UserViewModel, CurrentUserInputModel>({
      query: (model) => client.patchCurrentUser(model),
      invalidatesTags: ['currentUser'],
    }),
    patchCustomerTenant: builder.mutation<CustomerViewModel, { customerId: string, tenantId?: string }>({
      query: (rq) => client.patchCustomerTenant(rq.customerId, rq.tenantId),
      invalidatesTags: ['customers'],
    }),
    getAllUnits: builder.query<UnitAccountIndexViewModel[], void>({
      query: () => client.allUnits(),
    }),
    getUnits: builder.query<UnitAccountViewModel[], { tenantId: string; customerId: string }>({
      query: ({ tenantId, customerId }) => client.getUnits(tenantId, customerId),
    }),
    getCustomer: builder.query<CustomerViewModel, { tenantId: string; customerId: string }>({
      query: ({ tenantId, customerId }) => client.getCustomer(tenantId, customerId),
    }),
    getCustomerPersons: builder.query<PersonModel[], { customerId: string; roles: PersonRole[] }>({
      query: ({ customerId, roles }) => client.getCustomerPersons(customerId, roles),
    }),
    getCustomerManagers: builder.query<ManagerModel[], string>({
      query: (customerId) => client.getCustomerManagers(customerId),
    }),
    getTenant: builder.query<TenantViewModel, string>({
      query: (tenantId: string) => client.getTenant(tenantId),
      providesTags: ['tenants'],
    }),
    getTenants: builder.query<TenantViewModel[], void>({
      query: () => client.getTenants(),
      providesTags: ['tenants'],
    }),
    getTenantsStats: builder.query<TenantStatisticsViewModel[], void>({
      query: () => client.getTenantsStats(),
      providesTags: ['tenantStats'],
    }),
    getSettings: builder.query<UserSettingsViewModel, { remote?: boolean }>({
      query: async ({ remote }) => {
        if (remote) {
          const response = await client.getUserSettings()
          return response
        } else {
          const data = Object.keys(localStorage).reduce((r, setting) => {
            const entry = localStorage.getItem(setting)
            try {
              const obj = JSON.parse(entry!)
              r[setting] = obj
            } catch {
              r[setting] = entry
            }
            return r
          }, {} as UserSettingsViewModel)
          return { data }
        }
      },
    }),
    updateSettings: builder.mutation<void, { remote?: boolean; model: UserSettingsInputModel }>({
      query: async ({ remote, model }) => {
        if (remote) {
          await putUserSettingsDebounced(model)
        } else {
          Object.keys(model).forEach((setting) => {
            const obj = model[setting]
            let str = obj
            if (typeof obj !== 'string') {
              str = JSON.stringify(model[setting])
            }
            localStorage.setItem(setting, str)
          })
        }
        return { data: undefined }
      },
      onQueryStarted: ({ remote, model }, { dispatch }) => {
        dispatch(
          customersApi.util.updateQueryData('getSettings', { remote }, (draft) => {
            Object.assign(draft, model)
          })
        )
      },
    }),
    postTenant: builder.mutation<TenantViewModel, TenantInputModel>({
      query: (model) => client.postTenant(model),
      invalidatesTags: ['tenants', 'tenantStats'],
    }),
    putTenant: builder.mutation<TenantViewModel, { tenantId: string; model: TenantInputModel }>({
      query: ({ tenantId, model }) => client.putTenant(tenantId, model),
      invalidatesTags: ['tenants', 'tenantStats'],
    }),
    getTenantTags: builder.query<string[], void>({
      query: () => client.getTenantTags(),
    }),
    deleteTenant: builder.mutation<TenantViewModel[], string>({
      query: (tenantId) => client.deleteTenant(tenantId),
      invalidatesTags: ['tenants', 'tenantStats'],
    }),
    getApproverIds: builder.query<string[], { tenantId: string; model: ApproverEmailsInputModel }>({
      query: (rq) => client.approverIds(rq.tenantId, rq.model),
    }),
    validateApprovers: builder.query<ApproverValidationViewModel[], { tenantId: string, steps: ApproverValidationInputModel[] }>({
      query: (rq) => client.validateApprovers(rq.tenantId, rq.steps),
    }),
    searchUsers: builder.query<UserViewModel[], string>({
      query: (term) => client.searchUsers(term),
    }),
    getCustomersApiHealth: builder.query<{ status: 'Healthy' | 'Degraded' | 'Unhealthy' }, void>({
      query: () =>
        fetch(`${Constants.customerEndpoint}/health`)
          .then((r) => (r.ok ? r.json() : { status: 'Unhealthy' }))
          .then((r) => ({ data: r })),
    }),
  }),
  // Intended to auto update UserManagement/groups user stats counter. Requires RTK setup for UserAdminPaanel
  tagTypes: ['tenants', 'tenantStats', 'users', 'currentUser', 'userTags', 'customers'],
})

export const {
  useGetAllUnitsQuery,
  useGetAllCustomersQuery,
  useGetTenantQuery,
  useGetTenantsQuery,
  useGetUnitsQuery,
  useGetSettingsQuery,
  useGetCustomerPersonsQuery,
  useLazyGetCustomerPersonsQuery,
  useUpdateSettingsMutation,
  useLazyGetTenantsQuery,
  useLazyGetCustomerQuery,
  useGetTenantUsersQuery,
  useLazyGetTenantUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useGetUserTagsQuery,
  useLazyGetUserTagsQuery,
  usePostUserMutation,
  usePutUserMutation,
  usePatchUserEnabledMutation,
  useDeleteUserMutation,
  usePatchCurrentUserMutation,
  useLazyGetTenantQuery,
  useGetTenantsStatsQuery,
  useDeleteTenantMutation,
  usePostTenantMutation,
  usePutTenantMutation,
  useGetTenantTagsQuery,
  useGetCustomersApiHealthQuery,
  useLazySearchUsersQuery,
} = customersApi
